/**
 * FleetIntelligencePanel Component
 * Industry-grade fleet-wide AI intelligence summary
 * Redesigned for maximum visual engagement and clarity
 */

import { useMemo, useState, useEffect } from 'react'
import {
  Brain,
  AlertTriangle,
  CheckCircle,
  TrendingUp,
  TrendingDown,
  Activity,
  BarChart3,
  Cpu,
  Network,
  Zap,
  Target,
  Shield,
  Clock,
  ChevronRight,
  Info,
  Lightbulb,
  ArrowRight,
  FileText,
  HelpCircle,
  Sparkles,
  Eye,
  Bell,
  Gauge,
  Radio,
  WifiOff,
  RefreshCw,
  ServerOff,
  Loader2,
  AlertCircle
} from 'lucide-react'
import clsx from 'clsx'

// Connection status type
type ConnectionStatus = 'connected' | 'disconnected' | 'connecting' | 'error'

interface FleetAsset {
  assetId: string
  name: string
  healthScore: number
  anomalyScore: number
  riskLevel: 'low' | 'medium' | 'high' | 'critical'
  trend: number
  lastPrediction: string
  alertCount: number
}

interface FleetMetric {
  name: string
  value: number
  unit: string
  change: number
  target?: number
  status: 'good' | 'warning' | 'critical'
}

interface AIInsight {
  id: string
  type: 'pattern' | 'anomaly' | 'prediction' | 'recommendation'
  severity: 'info' | 'warning' | 'critical'
  title: string
  description: string
  affectedAssets: string[]
  confidence: number
  timestamp: string
  impact?: string
  rootCause?: string
  suggestedAction?: string
  timeframe?: string
}

// Insight type configuration with enhanced visual elements
const INSIGHT_CONFIG: Record<string, {
  label: string
  description: string
  icon: typeof Network
  color: string
  bgColor: string
  borderColor: string
  glowColor: string
  keyQuestion: string
}> = {
  pattern: {
    label: 'Pattern Detected',
    description: 'AI-identified recurring behaviors across your fleet',
    icon: Network,
    color: 'text-purple-400',
    bgColor: 'bg-purple-500/20',
    borderColor: 'border-purple-500/30',
    glowColor: 'shadow-purple-500/20',
    keyQuestion: 'What patterns are emerging?'
  },
  anomaly: {
    label: 'Anomaly Alert',
    description: 'Deviation from normal operating parameters',
    icon: AlertTriangle,
    color: 'text-amber-400',
    bgColor: 'bg-amber-500/20',
    borderColor: 'border-amber-500/30',
    glowColor: 'shadow-amber-500/20',
    keyQuestion: 'What is behaving unusually?'
  },
  prediction: {
    label: 'Predictive Insight',
    description: 'Forward-looking ML-based health forecast',
    icon: Brain,
    color: 'text-cyan-400',
    bgColor: 'bg-cyan-500/20',
    borderColor: 'border-cyan-500/30',
    glowColor: 'shadow-cyan-500/20',
    keyQuestion: 'What will happen next?'
  },
  recommendation: {
    label: 'Action Required',
    description: 'AI-generated maintenance suggestion',
    icon: Target,
    color: 'text-emerald-400',
    bgColor: 'bg-emerald-500/20',
    borderColor: 'border-emerald-500/30',
    glowColor: 'shadow-emerald-500/20',
    keyQuestion: 'What should you do?'
  }
}

// Model metric documentation with user-friendly explanations
const MODEL_METRICS = {
  accuracy: {
    description: 'How often the AI correctly predicts asset health status',
    benchmark: '>85%',
    interpretation: (val: number) => val >= 85 ? 'Excellent prediction accuracy' : val >= 75 ? 'Good, minor improvements possible' : 'Needs retraining'
  },
  precision: {
    description: 'When AI flags an issue, how often it\'s a real problem',
    benchmark: '>80%',
    interpretation: (val: number) => val >= 80 ? 'Low false alarm rate' : val >= 60 ? 'Some false alarms expected' : 'High false alarm rate'
  },
  recall: {
    description: 'How many actual problems the AI successfully catches',
    benchmark: '>85%',
    interpretation: (val: number) => val >= 85 ? 'Catches most issues' : val >= 70 ? 'May miss some issues' : 'Many issues go undetected'
  },
  f1Score: {
    description: 'Overall balance between catching issues and avoiding false alarms',
    benchmark: '>82%',
    interpretation: (val: number) => val >= 82 ? 'Well-balanced performance' : val >= 65 ? 'Acceptable balance' : 'Needs optimization'
  }
}

// Generate model performance summary based on metrics
const getModelPerformanceSummary = (perf: { accuracy: number; precision: number; recall: number; f1Score: number }) => {
  const avgScore = (perf.accuracy + perf.precision + perf.recall + perf.f1Score) / 4 * 100
  const accuracy = perf.accuracy * 100
  const recall = perf.recall * 100

  if (avgScore >= 85) {
    return 'AI models are performing excellently. Predictions are highly reliable for maintenance planning.'
  } else if (avgScore >= 75) {
    return `Models show good overall performance. ${recall < 85 ? 'Consider monitoring for missed anomalies.' : 'Predictions are trustworthy for decision-making.'}`
  } else if (avgScore >= 60) {
    return `Model performance is acceptable but could be improved. ${accuracy < 75 ? 'Prediction accuracy needs attention.' : recall < 70 ? 'Some failures may go undetected.' : 'Review training data quality.'}`
  } else {
    return 'Model retraining recommended. Current performance may lead to missed failures or false alarms.'
  }
}

interface FleetIntelligencePanelProps {
  fleetName: string
  totalAssets: number
  assets: FleetAsset[]
  metrics: FleetMetric[]
  insights: AIInsight[]
  modelPerformance: {
    accuracy: number
    precision: number
    recall: number
    f1Score: number
  }
  // Connection and loading states
  connectionStatus?: ConnectionStatus
  isLoading?: boolean
  isMockData?: boolean
  lastUpdated?: string
  error?: string | null
  onRetry?: () => void
  className?: string
}

export default function FleetIntelligencePanel({
  fleetName,
  totalAssets,
  assets,
  metrics: _metrics,
  insights,
  modelPerformance,
  connectionStatus = 'connected',
  isLoading = false,
  isMockData = false,
  lastUpdated,
  error = null,
  onRetry,
  className
}: FleetIntelligencePanelProps) {
  const [selectedInsight, setSelectedInsight] = useState<string | null>(null)
  const [showInsightHelp, setShowInsightHelp] = useState(false)
  const [activeMetric, setActiveMetric] = useState<string | null>(null)
  const [pulseActive, setPulseActive] = useState(true)

  // Check if offline or has error
  const isOffline = connectionStatus === 'disconnected' || connectionStatus === 'error'
  const hasError = !!error || isOffline

  // Pulse animation for live indicator (only when connected)
  useEffect(() => {
    if (connectionStatus !== 'connected') {
      setPulseActive(false)
      return
    }
    const interval = setInterval(() => {
      setPulseActive(p => !p)
    }, 2000)
    return () => clearInterval(interval)
  }, [connectionStatus])

  // Connection status display configuration
  const connectionDisplay = useMemo(() => {
    switch (connectionStatus) {
      case 'connected':
        return {
          icon: Radio,
          label: 'Live',
          color: 'text-emerald-400',
          bgColor: 'bg-emerald-500/20',
          borderColor: 'border-emerald-500/30',
          pulse: true,
          description: 'Real-time data stream active'
        }
      case 'connecting':
        return {
          icon: Loader2,
          label: 'Connecting',
          color: 'text-amber-400',
          bgColor: 'bg-amber-500/20',
          borderColor: 'border-amber-500/30',
          pulse: false,
          spin: true,
          description: 'Establishing connection to server...'
        }
      case 'error':
        return {
          icon: AlertCircle,
          label: 'Error',
          color: 'text-red-400',
          bgColor: 'bg-red-500/20',
          borderColor: 'border-red-500/30',
          pulse: false,
          description: error || 'API connection failed'
        }
      case 'disconnected':
      default:
        return {
          icon: WifiOff,
          label: 'Offline',
          color: 'text-surface-400',
          bgColor: 'bg-surface-700/50',
          borderColor: 'border-surface-600',
          pulse: false,
          description: 'Connection lost. Data may be outdated.'
        }
    }
  }, [connectionStatus, error])

  // Calculate distributions
  const healthDistribution = useMemo(() => ({
    healthy: assets.filter(a => a.healthScore >= 85).length,
    warning: assets.filter(a => a.healthScore >= 70 && a.healthScore < 85).length,
    critical: assets.filter(a => a.healthScore < 70).length
  }), [assets])

  const riskDistribution = useMemo(() => ({
    low: assets.filter(a => a.riskLevel === 'low').length,
    medium: assets.filter(a => a.riskLevel === 'medium').length,
    high: assets.filter(a => a.riskLevel === 'high').length,
    critical: assets.filter(a => a.riskLevel === 'critical').length
  }), [assets])

  const assetsByRisk = useMemo(() => {
    const order = { critical: 0, high: 1, medium: 2, low: 3 }
    return [...assets].sort((a, b) => order[a.riskLevel] - order[b.riskLevel])
  }, [assets])

  const avgFleetHealth = useMemo(() =>
    assets.reduce((sum, a) => sum + a.healthScore, 0) / assets.length
  , [assets])

  // Count insights by severity
  const insightCounts = useMemo(() => ({
    critical: insights.filter(i => i.severity === 'critical').length,
    warning: insights.filter(i => i.severity === 'warning').length,
    info: insights.filter(i => i.severity === 'info').length
  }), [insights])

  const getHealthColor = (score: number) =>
    score >= 85 ? 'text-emerald-400' : score >= 70 ? 'text-amber-400' : 'text-red-400'

  const getHealthBg = (score: number) =>
    score >= 85 ? 'bg-emerald-500' : score >= 70 ? 'bg-amber-500' : 'bg-red-500'

  return (
    <div className={clsx(
      'rounded-2xl border border-surface-600/50 bg-gradient-to-br from-surface-900 via-surface-900 to-surface-800 overflow-hidden shadow-2xl',
      className
    )}>
      {/* ═══════════════════════════════════════════════════════════════════════
          HERO HEADER - Premium gradient with key metrics
      ═══════════════════════════════════════════════════════════════════════ */}
      <div className="relative overflow-hidden">
        {/* Animated background */}
        <div className="absolute inset-0 bg-gradient-to-r from-purple-600/10 via-cyan-600/10 to-emerald-600/10" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-purple-500/5 via-transparent to-transparent" />

        <div className="relative p-6">
          <div className="flex items-start justify-between">
            {/* Title Section */}
            <div className="flex items-center gap-4">
              <div className="relative">
                <div className="p-3 rounded-xl bg-gradient-to-br from-purple-500/30 to-cyan-500/30 border border-purple-500/20">
                  <Brain size={28} className="text-purple-400" />
                </div>
                {/* Live indicator */}
                <div className={clsx(
                  'absolute -top-1 -right-1 w-3 h-3 rounded-full bg-emerald-500',
                  pulseActive && 'animate-pulse'
                )} />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <h2 className="text-xl font-bold text-white">Fleet Intelligence</h2>
                  <span className="px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider rounded-full bg-purple-500/20 text-purple-300 border border-purple-500/30">
                    AI-Powered
                  </span>
                  {/* Connection Status Badge */}
                  <div className={clsx(
                    'flex items-center gap-1.5 px-2 py-0.5 rounded-full border text-[10px] font-bold uppercase tracking-wider',
                    connectionDisplay.bgColor,
                    connectionDisplay.borderColor,
                    connectionDisplay.color
                  )}>
                    <connectionDisplay.icon
                      size={10}
                      className={clsx(
                        connectionDisplay.color,
                        (connectionDisplay as { spin?: boolean }).spin && 'animate-spin'
                      )}
                    />
                    {connectionDisplay.label}
                    {connectionDisplay.pulse && (
                      <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                    )}
                  </div>
                </div>
                <p className="text-sm text-surface-400 mt-1">
                  <span className="font-semibold text-surface-200">{fleetName}</span>
                  <span className="mx-2">•</span>
                  <span className="text-cyan-400 font-mono">{totalAssets}</span> Assets Monitored
                  {lastUpdated && (
                    <>
                      <span className="mx-2">•</span>
                      <Clock size={11} className="inline text-surface-500" />
                      <span className="text-surface-500 ml-1">{lastUpdated}</span>
                    </>
                  )}
                </p>
              </div>
            </div>

            {/* Key Metrics Pills */}
            <div className="flex items-center gap-3">
              {/* Fleet Health Metric */}
              <div className="flex flex-col items-center p-3 rounded-xl bg-surface-800/50 border border-surface-700/50 min-w-[100px]">
                <span className="text-[10px] font-medium text-surface-500 uppercase tracking-wider">Fleet Health</span>
                <span className={clsx(
                  'text-3xl font-bold font-mono mt-1',
                  getHealthColor(avgFleetHealth)
                )}>
                  {avgFleetHealth.toFixed(0)}%
                </span>
                <div className="w-full h-1.5 bg-surface-700 rounded-full mt-2 overflow-hidden">
                  <div
                    className={clsx('h-full rounded-full transition-all duration-500', getHealthBg(avgFleetHealth))}
                    style={{ width: `${avgFleetHealth}%` }}
                  />
                </div>
              </div>

              {/* AI Confidence Metric */}
              <div className="flex flex-col items-center p-3 rounded-xl bg-surface-800/50 border border-surface-700/50 min-w-[100px]">
                <span className="text-[10px] font-medium text-surface-500 uppercase tracking-wider">AI Confidence</span>
                <span className="text-3xl font-bold font-mono mt-1 text-cyan-400">
                  {(modelPerformance.accuracy * 100).toFixed(0)}%
                </span>
                <div className="flex items-center gap-1 mt-2">
                  <Sparkles size={12} className="text-cyan-400" />
                  <span className="text-[10px] text-surface-400">Ensemble Model</span>
                </div>
              </div>
            </div>
          </div>

          {/* Connection Status Banner - Shows when offline, error, or using mock data */}
          {(hasError || isMockData || isLoading) && (
            <div className={clsx(
              'mt-4 rounded-xl overflow-hidden animate-in fade-in slide-in-from-top-2 duration-300',
              hasError ? 'bg-gradient-to-r from-red-500/10 to-amber-500/10 border border-red-500/30' :
              isMockData ? 'bg-gradient-to-r from-amber-500/10 to-surface-800 border border-amber-500/30' :
              'bg-gradient-to-r from-cyan-500/10 to-surface-800 border border-cyan-500/30'
            )}>
              <div className="p-4">
                <div className="flex items-start gap-4">
                  {/* Status Icon */}
                  <div className={clsx(
                    'p-2.5 rounded-xl flex-shrink-0',
                    hasError ? 'bg-red-500/20' :
                    isMockData ? 'bg-amber-500/20' : 'bg-cyan-500/20'
                  )}>
                    {isLoading ? (
                      <Loader2 size={24} className="text-cyan-400 animate-spin" />
                    ) : hasError ? (
                      <ServerOff size={24} className="text-red-400" />
                    ) : (
                      <AlertTriangle size={24} className="text-amber-400" />
                    )}
                  </div>

                  {/* Message Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <h4 className={clsx(
                        'font-semibold',
                        hasError ? 'text-red-400' :
                        isMockData ? 'text-amber-400' : 'text-cyan-400'
                      )}>
                        {isLoading ? 'Loading Fleet Data' :
                         hasError ? 'Connection Issue Detected' :
                         'Displaying Demo Data'}
                      </h4>
                      {hasError && (
                        <span className="px-2 py-0.5 text-[10px] font-bold uppercase rounded bg-red-500/20 text-red-400 animate-pulse">
                          Action Required
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-surface-300 mt-1 leading-relaxed">
                      {isLoading ? (
                        'Fetching latest fleet intelligence data from the server...'
                      ) : hasError ? (
                        <>
                          {connectionDisplay.description}
                          <span className="text-surface-400 ml-1">
                            • Insights shown may be outdated. Check that the backend server is running and accessible.
                          </span>
                        </>
                      ) : (
                        <>
                          Showing simulated data for demonstration purposes.
                          <span className="text-surface-400 ml-1">
                            • Connect to the backend server for live fleet monitoring.
                          </span>
                        </>
                      )}
                    </p>

                    {/* Action Buttons */}
                    {!isLoading && (
                      <div className="flex items-center gap-3 mt-3">
                        {onRetry && (
                          <button
                            onClick={onRetry}
                            className={clsx(
                              'flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium transition-all',
                              hasError
                                ? 'bg-red-500/20 text-red-400 hover:bg-red-500/30 border border-red-500/30'
                                : 'bg-cyan-500/20 text-cyan-400 hover:bg-cyan-500/30 border border-cyan-500/30'
                            )}
                          >
                            <RefreshCw size={14} />
                            Retry Connection
                          </button>
                        )}
                        <button className="text-xs text-surface-400 hover:text-surface-300 transition-colors">
                          View connection status →
                        </button>
                      </div>
                    )}
                  </div>

                  {/* Timestamp */}
                  {lastUpdated && !isLoading && (
                    <div className="text-right flex-shrink-0">
                      <p className="text-[10px] text-surface-500 uppercase tracking-wider">Last Updated</p>
                      <p className="text-sm font-mono text-surface-300 mt-0.5">{lastUpdated}</p>
                    </div>
                  )}
                </div>

                {/* Progress bar for loading state */}
                {isLoading && (
                  <div className="mt-3 h-1 bg-surface-700 rounded-full overflow-hidden">
                    <div className="h-full bg-cyan-500 rounded-full animate-pulse" style={{ width: '60%' }} />
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Quick Status Bar */}
          <div className="flex items-center gap-4 mt-6 p-3 rounded-xl bg-surface-800/30 border border-surface-700/30">
            <StatusPill
              icon={CheckCircle}
              label="Healthy"
              value={healthDistribution.healthy}
              color="emerald"
            />
            <div className="w-px h-8 bg-surface-700" />
            <StatusPill
              icon={AlertTriangle}
              label="Warning"
              value={healthDistribution.warning}
              color="amber"
            />
            <div className="w-px h-8 bg-surface-700" />
            <StatusPill
              icon={Activity}
              label="Critical"
              value={healthDistribution.critical}
              color="red"
            />
            <div className="flex-1" />
            <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-surface-700/50">
              <Bell size={14} className={insightCounts.critical > 0 ? 'text-red-400' : 'text-surface-400'} />
              <span className="text-xs text-surface-300">
                <span className={clsx('font-bold', insightCounts.critical > 0 && 'text-red-400')}>
                  {insightCounts.critical + insightCounts.warning}
                </span> Active Alerts
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* ═══════════════════════════════════════════════════════════════════════
          MAIN CONTENT - Two Column Layout
      ═══════════════════════════════════════════════════════════════════════ */}
      <div className="grid grid-cols-3 divide-x divide-surface-700/50">

        {/* LEFT COLUMN - AI Insights (2/3 width) */}
        <div className="col-span-2 p-5">

          {/* Section Header */}
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-gradient-to-br from-cyan-500/20 to-purple-500/20">
                <Cpu size={18} className="text-cyan-400" />
              </div>
              <div>
                <h3 className="font-semibold text-white flex items-center gap-2">
                  AI-Generated Insights
                  <button
                    onClick={() => setShowInsightHelp(!showInsightHelp)}
                    className="p-1 rounded-lg hover:bg-surface-700 text-surface-500 hover:text-surface-300 transition-colors"
                  >
                    <HelpCircle size={14} />
                  </button>
                </h3>
                <p className="text-xs text-surface-500">Real-time analysis from ensemble ML models</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <span className="px-2 py-1 rounded-lg bg-surface-800 text-xs text-surface-400">
                {insights.length} insights
              </span>
            </div>
          </div>

          {/* Help Panel */}
          {showInsightHelp && (
            <div className="mb-4 p-4 rounded-xl bg-gradient-to-r from-surface-800 to-surface-800/50 border border-surface-600">
              <div className="flex items-center gap-2 mb-3">
                <Info size={16} className="text-cyan-400" />
                <span className="font-medium text-white">Understanding AI Insights</span>
              </div>
              <div className="grid grid-cols-2 gap-3">
                {Object.entries(INSIGHT_CONFIG).map(([key, config]) => {
                  const Icon = config.icon
                  return (
                    <div key={key} className={clsx('flex items-start gap-3 p-3 rounded-lg', config.bgColor, config.borderColor, 'border')}>
                      <Icon size={16} className={config.color} />
                      <div>
                        <p className={clsx('text-xs font-semibold', config.color)}>{config.label}</p>
                        <p className="text-xs text-surface-400 mt-0.5">{config.description}</p>
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>
          )}

          {/* Executive Summary Box */}
          <div className={clsx(
            'mb-5 p-4 rounded-xl border',
            hasError
              ? 'bg-gradient-to-r from-surface-800 to-surface-800/50 border-surface-700'
              : 'bg-gradient-to-r from-cyan-500/10 via-purple-500/10 to-emerald-500/10 border-cyan-500/20'
          )}>
            <div className="flex items-start gap-3">
              <div className={clsx(
                'p-2 rounded-lg border',
                hasError
                  ? 'bg-surface-700/50 border-surface-600'
                  : 'bg-cyan-500/20 border-cyan-500/30'
              )}>
                <Lightbulb size={18} className={hasError ? 'text-surface-400' : 'text-cyan-400'} />
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={clsx(
                    'text-xs font-bold uppercase tracking-wider',
                    hasError ? 'text-surface-400' : 'text-cyan-400'
                  )}>
                    Executive Summary
                  </span>
                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-surface-700 text-surface-400">
                    Auto-generated
                  </span>
                  {hasError && (
                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-400 border border-amber-500/30">
                      ⚠ Data may be outdated
                    </span>
                  )}
                  {isMockData && !hasError && (
                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-purple-500/20 text-purple-400 border border-purple-500/30">
                      Demo Mode
                    </span>
                  )}
                </div>
                <p className={clsx(
                  'text-sm mt-2 leading-relaxed',
                  hasError ? 'text-surface-400' : 'text-surface-200'
                )}>
                  {hasError ? (
                    <>
                      <span className="text-amber-400">Unable to generate real-time summary.</span>
                      {' '}Displaying last known status. Reconnect to the backend server for current fleet intelligence.
                      {insightCounts.critical > 0 && (
                        <span className="text-red-400 font-medium ml-1">
                          • {insightCounts.critical} critical alert(s) were pending.
                        </span>
                      )}
                    </>
                  ) : insightCounts.critical > 0 ? (
                    <>
                      <span className="font-semibold text-red-400">{insightCounts.critical} critical issue{insightCounts.critical > 1 ? 's' : ''}</span> require immediate attention.
                      Potential impact on <span className="font-semibold text-white">{assets.filter(a => a.riskLevel === 'critical').length} assets</span>.
                      <span className="text-emerald-400"> Recommended: Review high-priority alerts and initiate corrective actions within 4 hours.</span>
                    </>
                  ) : insightCounts.warning > 0 ? (
                    <>
                      <span className="font-semibold text-amber-400">{insightCounts.warning} warning{insightCounts.warning > 1 ? 's' : ''}</span> detected across the fleet.
                      Schedule preventive maintenance for affected assets within the next <span className="font-semibold text-white">48 hours</span> to maintain optimal reliability.
                    </>
                  ) : (
                    <>
                      <span className="font-semibold text-emerald-400">Fleet operating normally.</span> All systems within expected parameters.
                      Continue standard monitoring protocols. Next scheduled maintenance window: <span className="font-semibold text-white">72 hours</span>.
                    </>
                  )}
                </p>
              </div>
            </div>
          </div>

          {/* Insight Cards */}
          <div className="space-y-3">
            {/* Empty State - When no insights available */}
            {insights.length === 0 && (
              <div className={clsx(
                'p-8 rounded-xl border text-center',
                hasError
                  ? 'bg-surface-800/30 border-surface-700'
                  : 'bg-gradient-to-br from-surface-800/50 to-surface-800/30 border-surface-700/50'
              )}>
                <div className={clsx(
                  'inline-flex p-4 rounded-2xl mb-4',
                  hasError ? 'bg-red-500/10' : 'bg-surface-700/50'
                )}>
                  {hasError ? (
                    <ServerOff size={40} className="text-red-400" />
                  ) : (
                    <Brain size={40} className="text-surface-500" />
                  )}
                </div>
                <h4 className={clsx(
                  'text-lg font-semibold mb-2',
                  hasError ? 'text-red-400' : 'text-surface-300'
                )}>
                  {hasError
                    ? 'Unable to Generate Insights'
                    : 'No Active Insights'
                  }
                </h4>
                <p className="text-sm text-surface-400 max-w-md mx-auto leading-relaxed">
                  {hasError ? (
                    <>
                      API connection failed. Check that the backend server is running and accessible.
                      <br />
                      <span className="text-surface-500">
                        Once connected, AI insights will appear automatically.
                      </span>
                    </>
                  ) : (
                    <>
                      All fleet systems are operating within normal parameters.
                      <br />
                      <span className="text-emerald-400">
                        The AI model will generate insights when anomalies are detected.
                      </span>
                    </>
                  )}
                </p>
                {hasError && onRetry && (
                  <button
                    onClick={onRetry}
                    className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-red-500/20 text-red-400 hover:bg-red-500/30 border border-red-500/30 transition-all"
                  >
                    <RefreshCw size={16} />
                    Retry Connection
                  </button>
                )}
              </div>
            )}

            {insights.slice(0, 5).map((insight) => {
              const config = INSIGHT_CONFIG[insight.type] || INSIGHT_CONFIG.pattern
              const Icon = config.icon
              const isExpanded = selectedInsight === insight.id

              return (
                <div
                  key={insight.id}
                  onClick={() => setSelectedInsight(isExpanded ? null : insight.id)}
                  className={clsx(
                    'group relative rounded-xl border cursor-pointer transition-all duration-300',
                    config.borderColor,
                    isExpanded ? [config.bgColor, 'shadow-lg', config.glowColor] : 'bg-surface-800/30 hover:bg-surface-800/50',
                  )}
                >
                  {/* Severity Indicator Bar */}
                  <div className={clsx(
                    'absolute left-0 top-0 bottom-0 w-1 rounded-l-xl',
                    insight.severity === 'critical' ? 'bg-red-500' :
                    insight.severity === 'warning' ? 'bg-amber-500' : 'bg-cyan-500'
                  )} />

                  <div className="p-4 pl-5">
                    <div className="flex items-start gap-3">
                      {/* Icon */}
                      <div className={clsx('p-2 rounded-lg transition-colors', config.bgColor, config.borderColor, 'border')}>
                        <Icon size={16} className={config.color} />
                      </div>

                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        {/* Header Row */}
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className={clsx('text-[10px] font-bold uppercase tracking-wider', config.color)}>
                            {config.label}
                          </span>
                          <span className="px-1.5 py-0.5 text-[10px] rounded bg-surface-700/50 text-surface-400 font-mono">
                            {(insight.confidence * 100).toFixed(0)}% conf
                          </span>
                          {insight.severity === 'critical' && (
                            <span className="px-1.5 py-0.5 text-[10px] rounded bg-red-500/20 text-red-400 font-bold animate-pulse">
                              URGENT
                            </span>
                          )}
                          {insight.timeframe && (
                            <span className="flex items-center gap-1 px-1.5 py-0.5 text-[10px] rounded bg-surface-700/50 text-surface-400">
                              <Clock size={10} />
                              {insight.timeframe}
                            </span>
                          )}
                        </div>

                        {/* Title */}
                        <h4 className="font-semibold text-white mt-1.5 group-hover:text-cyan-200 transition-colors">
                          {insight.title}
                        </h4>

                        {/* Description */}
                        <p className="text-sm text-surface-400 mt-1 line-clamp-2">
                          {insight.description}
                        </p>

                        {/* Expanded Content */}
                        {isExpanded && (
                          <div className="mt-4 pt-4 border-t border-surface-700/50 space-y-4 animate-in fade-in duration-300">
                            {/* Impact & Root Cause Grid */}
                            <div className="grid grid-cols-2 gap-3">
                              {insight.impact && (
                                <div className="p-3 rounded-lg bg-surface-900/50 border border-surface-700/50">
                                  <div className="flex items-center gap-2 mb-2">
                                    <Gauge size={12} className="text-amber-400" />
                                    <span className="text-[10px] font-bold text-amber-400 uppercase tracking-wider">
                                      Business Impact
                                    </span>
                                  </div>
                                  <p className="text-xs text-surface-300 leading-relaxed">{insight.impact}</p>
                                </div>
                              )}
                              {insight.rootCause && (
                                <div className="p-3 rounded-lg bg-surface-900/50 border border-surface-700/50">
                                  <div className="flex items-center gap-2 mb-2">
                                    <FileText size={12} className="text-purple-400" />
                                    <span className="text-[10px] font-bold text-purple-400 uppercase tracking-wider">
                                      Root Cause
                                    </span>
                                  </div>
                                  <p className="text-xs text-surface-300 leading-relaxed">{insight.rootCause}</p>
                                </div>
                              )}
                            </div>

                            {/* Recommended Action */}
                            {insight.suggestedAction && (
                              <div className="p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/20">
                                <div className="flex items-center gap-2 mb-2">
                                  <ArrowRight size={12} className="text-emerald-400" />
                                  <span className="text-[10px] font-bold text-emerald-400 uppercase tracking-wider">
                                    Recommended Action
                                  </span>
                                </div>
                                <p className="text-sm text-white leading-relaxed">{insight.suggestedAction}</p>
                              </div>
                            )}

                            {/* Affected Assets */}
                            <div>
                              <span className="text-[10px] font-bold text-surface-500 uppercase tracking-wider">
                                Affected Assets ({insight.affectedAssets.length})
                              </span>
                              <div className="flex flex-wrap gap-1.5 mt-2">
                                {insight.affectedAssets.map(asset => (
                                  <span
                                    key={asset}
                                    className="px-2 py-1 text-xs rounded-lg bg-surface-700/50 text-surface-300 font-mono border border-surface-600/50 hover:border-cyan-500/50 hover:text-cyan-300 cursor-pointer transition-colors"
                                  >
                                    {asset}
                                  </span>
                                ))}
                              </div>
                            </div>

                            {/* Footer */}
                            <div className="flex items-center justify-between pt-3 border-t border-surface-700/50 text-[10px] text-surface-500">
                              <span className="flex items-center gap-1">
                                <Clock size={10} />
                                Generated: {insight.timestamp}
                              </span>
                              <span className="flex items-center gap-1">
                                <Brain size={10} />
                                Ensemble Model v2.1
                              </span>
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Expand Arrow */}
                      <ChevronRight
                        size={18}
                        className={clsx(
                          'text-surface-500 transition-transform duration-300 flex-shrink-0',
                          isExpanded && 'rotate-90 text-cyan-400'
                        )}
                      />
                    </div>
                  </div>
                </div>
              )
            })}
          </div>

          {/* Model Performance Section */}
          <div className="mt-6 pt-6 border-t border-surface-700/50">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <BarChart3 size={16} className="text-purple-400" />
                <span className="font-semibold text-white">Model Performance</span>
              </div>
              <span className="text-[10px] text-surface-500 uppercase tracking-wider">
                30-Day Rolling Evaluation
              </span>
            </div>

            <div className="grid grid-cols-4 gap-3">
              {[
                { key: 'accuracy', name: 'Accuracy', value: modelPerformance.accuracy, icon: Target, color: 'cyan' },
                { key: 'precision', name: 'Precision', value: modelPerformance.precision, icon: Zap, color: 'purple' },
                { key: 'recall', name: 'Recall', value: modelPerformance.recall, icon: Eye, color: 'emerald' },
                { key: 'f1Score', name: 'F1 Score', value: modelPerformance.f1Score, icon: Shield, color: 'amber' }
              ].map(metric => {
                const pct = metric.value * 100
                const status = pct >= 85 ? 'good' : pct >= 75 ? 'warning' : 'critical'
                const isActive = activeMetric === metric.key
                const doc = MODEL_METRICS[metric.key as keyof typeof MODEL_METRICS]

                return (
                  <div
                    key={metric.key}
                    className={clsx(
                      'relative p-3 rounded-xl border transition-all cursor-help',
                      isActive
                        ? `border-${metric.color}-500/50 bg-${metric.color}-500/10 shadow-lg`
                        : 'border-surface-700/50 bg-surface-800/30 hover:bg-surface-800/50'
                    )}
                    onMouseEnter={() => setActiveMetric(metric.key)}
                    onMouseLeave={() => setActiveMetric(null)}
                  >
                    <div className="flex items-center gap-2 mb-2">
                      <metric.icon size={14} className={`text-${metric.color}-400`} />
                      <span className="text-xs text-surface-400">{metric.name}</span>
                    </div>
                    <p className={clsx(
                      'text-2xl font-bold font-mono',
                      status === 'good' ? 'text-emerald-400' :
                      status === 'warning' ? 'text-amber-400' : 'text-red-400'
                    )}>
                      {pct.toFixed(1)}%
                    </p>
                    <div className="mt-2 h-1.5 bg-surface-700 rounded-full overflow-hidden">
                      <div
                        className={clsx(
                          'h-full rounded-full transition-all duration-500',
                          status === 'good' ? 'bg-emerald-500' :
                          status === 'warning' ? 'bg-amber-500' : 'bg-red-500'
                        )}
                        style={{ width: `${pct}%` }}
                      />
                    </div>

                    {/* Tooltip */}
                    {isActive && doc && (
                      <div className="absolute z-30 left-0 right-0 top-full mt-2 p-3 rounded-xl bg-surface-800 border border-surface-600 shadow-xl">
                        <p className="text-xs text-surface-300">{doc.description}</p>
                        <p className="text-[10px] text-cyan-400 mt-2">Industry benchmark: {doc.benchmark}</p>
                      </div>
                    )}
                  </div>
                )
              })}
            </div>

            {/* AI Summary for Model Performance */}
            <div className="mt-4 p-3 rounded-xl bg-gradient-to-r from-purple-500/10 to-surface-800/50 border border-purple-500/20">
              <div className="flex items-start gap-2">
                <Brain size={14} className="text-purple-400 mt-0.5 flex-shrink-0" />
                <p className="text-xs text-surface-300 leading-relaxed">
                  {getModelPerformanceSummary(modelPerformance)}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* RIGHT COLUMN - Priority Queue (1/3 width) */}
        <div className="p-5 bg-surface-800/20">
          {/* Header */}
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <div className="p-1.5 rounded-lg bg-amber-500/20">
                <AlertTriangle size={14} className="text-amber-400" />
              </div>
              <span className="font-semibold text-white text-sm">Priority Queue</span>
            </div>
          </div>

          {/* Summary */}
          <div className="p-3 rounded-xl bg-gradient-to-r from-surface-800 to-surface-800/50 border border-surface-700/50 mb-4">
            <p className="text-xs text-surface-300 leading-relaxed">
              {riskDistribution.critical > 0 || riskDistribution.high > 0 ? (
                <>
                  <span className="font-bold text-red-400">{riskDistribution.critical + riskDistribution.high}</span> asset(s) require immediate review
                </>
              ) : (
                <span className="text-emerald-400">All assets within acceptable risk levels</span>
              )}
            </p>
          </div>

          {/* Asset Cards */}
          <div className="space-y-2 max-h-[380px] overflow-y-auto pr-1 scrollbar-thin scrollbar-thumb-surface-600 scrollbar-track-transparent">
            {assetsByRisk.slice(0, 8).map((asset) => (
              <div
                key={asset.assetId}
                className={clsx(
                  'p-3 rounded-xl border transition-all hover:scale-[1.02] cursor-pointer',
                  asset.riskLevel === 'critical' ? 'border-red-500/30 bg-red-500/10' :
                  asset.riskLevel === 'high' ? 'border-amber-500/30 bg-amber-500/10' :
                  'border-surface-700/50 bg-surface-800/30 hover:bg-surface-800/50'
                )}
              >
                <div className="flex items-center justify-between mb-2">
                  <span className="font-mono text-sm font-semibold text-white">{asset.assetId}</span>
                  <span className={clsx(
                    'text-[10px] font-bold uppercase px-1.5 py-0.5 rounded',
                    asset.riskLevel === 'critical' ? 'bg-red-500/20 text-red-400' :
                    asset.riskLevel === 'high' ? 'bg-amber-500/20 text-amber-400' :
                    asset.riskLevel === 'medium' ? 'bg-cyan-500/20 text-cyan-400' :
                    'bg-emerald-500/20 text-emerald-400'
                  )}>
                    {asset.riskLevel}
                  </span>
                </div>

                <div className="flex items-center justify-between text-xs mb-1.5">
                  <span className="text-surface-500">Health Score</span>
                  <span className={clsx('font-mono font-bold', getHealthColor(asset.healthScore))}>
                    {asset.healthScore}%
                  </span>
                </div>

                <div className="h-1.5 bg-surface-700 rounded-full overflow-hidden">
                  <div
                    className={clsx('h-full rounded-full transition-all', getHealthBg(asset.healthScore))}
                    style={{ width: `${asset.healthScore}%` }}
                  />
                </div>

                <div className="flex items-center justify-between mt-2 text-[10px]">
                  <span className="flex items-center gap-1 text-surface-500">
                    <Clock size={10} />
                    {asset.lastPrediction}
                  </span>
                  <span className={clsx(
                    'flex items-center gap-1 font-mono',
                    asset.trend >= 0 ? 'text-emerald-400' : 'text-red-400'
                  )}>
                    {asset.trend >= 0 ? <TrendingUp size={10} /> : <TrendingDown size={10} />}
                    {asset.trend > 0 ? '+' : ''}{asset.trend}%
                  </span>
                </div>
              </div>
            ))}
          </div>

          {/* Risk Distribution */}
          <div className="mt-4 p-3 rounded-xl bg-surface-800/50 border border-surface-700/50">
            <div className="flex items-center justify-between mb-3">
              <span className="text-xs font-semibold text-surface-300">Risk Distribution</span>
              <span className="text-[10px] text-surface-500">
                {((riskDistribution.low / totalAssets) * 100).toFixed(0)}% low risk
              </span>
            </div>

            {/* Stacked Bar */}
            <div className="flex items-center gap-0.5 h-3 rounded-full overflow-hidden bg-surface-700">
              {riskDistribution.critical > 0 && (
                <div className="h-full bg-red-500" style={{ width: `${(riskDistribution.critical / totalAssets) * 100}%` }} />
              )}
              {riskDistribution.high > 0 && (
                <div className="h-full bg-amber-500" style={{ width: `${(riskDistribution.high / totalAssets) * 100}%` }} />
              )}
              {riskDistribution.medium > 0 && (
                <div className="h-full bg-cyan-500" style={{ width: `${(riskDistribution.medium / totalAssets) * 100}%` }} />
              )}
              {riskDistribution.low > 0 && (
                <div className="h-full bg-emerald-500" style={{ width: `${(riskDistribution.low / totalAssets) * 100}%` }} />
              )}
            </div>

            {/* Legend */}
            <div className="grid grid-cols-4 gap-2 mt-3">
              {[
                { label: 'Crit', value: riskDistribution.critical, color: 'bg-red-500' },
                { label: 'High', value: riskDistribution.high, color: 'bg-amber-500' },
                { label: 'Med', value: riskDistribution.medium, color: 'bg-cyan-500' },
                { label: 'Low', value: riskDistribution.low, color: 'bg-emerald-500' }
              ].map(item => (
                <div key={item.label} className="flex items-center gap-1">
                  <div className={clsx('w-2 h-2 rounded-sm', item.color)} />
                  <span className="text-[10px] text-surface-400">
                    {item.value} {item.label}
                  </span>
                </div>
              ))}
            </div>

            {/* Interpretation */}
            <div className="mt-3 pt-3 border-t border-surface-700/50">
              <p className="text-[10px] text-surface-500 leading-relaxed">
                {riskDistribution.critical > 0
                  ? 'Critical assets require immediate intervention.'
                  : riskDistribution.high > 0
                  ? 'Schedule maintenance for high-risk assets within 48h.'
                  : 'Fleet risk profile acceptable. Continue monitoring.'}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

// Status Pill Component
function StatusPill({
  icon: Icon,
  label,
  value,
  color
}: {
  icon: typeof CheckCircle
  label: string
  value: number
  color: 'emerald' | 'amber' | 'red'
}) {
  const colorClasses = {
    emerald: { bg: 'bg-emerald-500/20', text: 'text-emerald-400', border: 'border-emerald-500/30' },
    amber: { bg: 'bg-amber-500/20', text: 'text-amber-400', border: 'border-amber-500/30' },
    red: { bg: 'bg-red-500/20', text: 'text-red-400', border: 'border-red-500/30' }
  }
  const c = colorClasses[color]

  return (
    <div className="flex items-center gap-3">
      <div className={clsx('p-2 rounded-lg border', c.bg, c.border)}>
        <Icon size={16} className={c.text} />
      </div>
      <div>
        <p className="text-[10px] text-surface-500 uppercase tracking-wider">{label}</p>
        <p className={clsx('text-xl font-bold font-mono', c.text)}>{value}</p>
      </div>
    </div>
  )
}
